import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { EXTENSIONS, STATUS_LABEL } from "@/lib/extensions";
import BaseProfileForm from "@/components/BaseProfileForm";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, ShieldAlert, Clock, Mail } from "lucide-react";

const STATUS_CLS = {
  none: "bg-slate-100 text-slate-500", draft: "bg-amber-50 text-amber-700",
  pending_review: "bg-blue-50 text-blue-700", active: "bg-emerald-50 text-emerald-700", rejected: "bg-red-50 text-red-700",
};

export function StatusPill({ status, lang, testid }) {
  return <span className={`text-[11px] font-display uppercase tracking-widest px-2.5 py-1 rounded-full ${STATUS_CLS[status]}`} data-testid={testid}>{STATUS_LABEL[status][lang]}</span>;
}

export default function Profile() {
  const { t, lang, setLang } = useLang();
  const a = t.account;
  const { refreshMe } = useAuth();
  const [base, setBase] = useState(null);
  const [exts, setExts] = useState([]);
  const [inv, setInv] = useState({ sent: [], received: [] });
  const [editing, setEditing] = useState(false);

  const load = () => {
    api.get("/profile/base").then((r) => setBase(r.data));
    api.get("/profile/extensions").then((r) => setExts(r.data));
    api.get("/profile/invitations").then((r) => setInv(r.data));
  };
  useEffect(() => { load(); }, []);

  const accept = async (id) => {
    await api.post(`/profile/invitations/${id}/accept`);
    toast.success(a.invitationAccepted); load();
  };

  const statusOf = (k) => exts.find((e) => e.type === k)?.status || "none";
  const ident = base?.identity_status || "none";
  const IdentIcon = ident === "verified" ? ShieldCheck : ident === "pending" ? Clock : ShieldAlert;

  if (!base) return <div className="max-w-5xl mx-auto px-6 py-20"><div className="h-64 bg-slate-50 animate-pulse rounded-2xl" /></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid="account-page">
      <p className="eyebrow">Bevela</p>
      <h1 className="font-serif text-4xl lg:text-5xl mt-3 text-[#0B192C]" data-testid="account-title">{a.title}</h1>

      {inv.received.length > 0 && (
        <div className="mt-8 rounded-2xl border border-[#C2A878]/50 bg-[#C2A878]/10 p-6" data-testid="account-invitations">
          <p className="eyebrow">{a.invitations}</p>
          {inv.received.map((i) => (
            <div key={i.id} className="mt-3 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-sm text-slate-700"><Mail className="inline h-4 w-4 mr-2" />{i.inviter_name} {a[i.kind === "coowner" ? "invitedCoowner" : "invitedGroup"]}</p>
              <button onClick={() => accept(i.id)} className="btn-primary text-sm" data-testid={`invitation-accept-${i.id}`}>{a.accept}</button>
            </div>
          ))}
        </div>
      )}

      {/* Perfil Base */}
      <section className="mt-12 card-clean p-8" data-testid="account-base">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="eyebrow">{a.baseProfile}</p>
            <p className="font-serif text-3xl mt-2 text-[#0B192C]">{base.first_name} {base.last_name}</p>
            <p className="text-sm text-slate-500 mt-1">{base.email} · {base.phone || "—"}</p>
            <div className={`mt-4 inline-flex items-center gap-2 text-xs font-display px-3 py-1.5 rounded-full ${ident === "verified" ? "bg-emerald-50 text-emerald-700" : ident === "pending" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-500"}`} data-testid="account-identity-status">
              <IdentIcon className="h-3.5 w-3.5" /> {a.identity[ident]}
            </div>
          </div>
          <div className="text-right">
            <p className="font-serif text-3xl text-[#0B192C]">{base.completeness}%</p>
            <p className="text-xs text-slate-400 font-display uppercase tracking-widest">{a.complete}</p>
            <button onClick={() => setEditing((v) => !v)} className="btn-outline text-sm mt-4" data-testid="account-edit-base">{editing ? a.cancel : a.edit}</button>
          </div>
        </div>
        {!editing ? (
          <dl className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
            <div><dt className="label-elegant">{a.city}</dt><dd className="text-slate-700">{base.residence_city || "—"}{base.residence_region ? `, ${base.residence_region}` : ""}</dd></div>
            <div><dt className="label-elegant">{a.zones}</dt><dd className="text-slate-700">{(base.nautical_zones || []).join(" · ") || "—"}</dd></div>
            <div><dt className="label-elegant">{a.experience}</dt><dd className="text-slate-700">{base.experience_level ? a.expLevels[base.experience_level] : "—"}{base.license ? ` · ${base.license}` : ""}</dd></div>
          </dl>
        ) : (
          <div className="mt-10 border-t border-slate-100 pt-10">
            <BaseProfileForm base={base} onSaved={(d) => { setBase(d); setEditing(false); refreshMe(); toast.success(a.saved); }} testid="acc-base" />
          </div>
        )}
      </section>

      {/* Extensiones */}
      <section className="mt-12" data-testid="account-extensions">
        <p className="eyebrow">{a.extensions}</p>
        <h2 className="font-serif text-3xl mt-2 text-[#0B192C]">{a.wantAlso}</h2>
        <p className="mt-2 text-slate-500 text-sm max-w-2xl">{a.noRepeat}</p>
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {Object.entries(EXTENSIONS).map(([k, ext]) => {
            const st = statusOf(k);
            return (
              <Link key={k} to={`/perfil/extension/${k}`} className="card-clean p-7 hover:border-slate-300 transition-colors group" data-testid={`ext-card-${k}`}>
                <div className="flex items-center justify-between">
                  <StatusPill status={st} lang={lang} testid={`ext-status-${k}`} />
                  <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-[#0B192C] transition-colors" />
                </div>
                <h3 className="font-serif text-2xl text-[#0B192C] mt-5">{ext.title[lang]}</h3>
                <p className="mt-2 text-sm text-slate-500">{ext.desc[lang]}</p>
                <p className="mt-5 text-sm font-display text-[#C2A878]">{st === "none" ? a.activate : st === "draft" ? a.completeIt : a.view}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="mt-12 card-clean p-8 flex items-center justify-between gap-6 flex-wrap">
        <div><p className="eyebrow">{t.profile.language}</p></div>
        <div className="flex gap-2">
          <button onClick={() => setLang("es")} className={`px-4 py-1.5 rounded-full text-sm ${lang === "es" ? "bg-[#0B192C] text-white" : "border border-slate-200"}`}>Español</button>
          <button onClick={() => setLang("en")} className={`px-4 py-1.5 rounded-full text-sm ${lang === "en" ? "bg-[#0B192C] text-white" : "border border-slate-200"}`}>English</button>
        </div>
      </section>
    </div>
  );
}
